import * as React from 'react';
import {
  getAskAIExtraPageContext,
  patchAskAIExtraPageContext,
  removeAskAIExtraPageContextKey,
} from './ask-ai-store';


export type AskAIExtraPageContextValue =
  | string
  | number
  | boolean
  | null
  | string[];
export type AskAIExtraPageContext = Record<
  string,
  AskAIExtraPageContextValue | undefined
>;

/**
 * Bind additional `context.page_context` key/values to the lifecycle of a React component.
 *
 * Behavior:
 * - On mount/update: patches provided key/values into AskAI extra page context.
 * - On unmount: removes ONLY the keys this hook last applied (safely: only if unchanged).
 *
 * Notes:
 * - Values of `undefined` are ignored (not sent).
 * - If you create the object inline every render, memoize it to avoid unnecessary effects.
 */
export function useAskAIExtraPageContext(extra?: AskAIExtraPageContext): void {
  const lastAppliedRef = React.useRef<
    Record<string, AskAIExtraPageContextValue>
  >({});

  React.useEffect(() => {
    const prev = lastAppliedRef.current;
    const next: Record<string, AskAIExtraPageContextValue> = {};
    for (const [k, v] of Object.entries(extra ?? {})) {
      if (v !== undefined) {
        next[k] = v;
      }
    }

    // Apply changes (added/updated keys).
    const patch: Record<string, AskAIExtraPageContextValue> = {};
    for (const [k, v] of Object.entries(next)) {
      if (prev[k] !== v) {
        patch[k] = v;
      }
    }
    if (Object.keys(patch).length) {
      patchAskAIExtraPageContext(patch);
    }

    // Remove keys that this hook previously set but no longer sets.
    const removedKeys: string[] = [];
    for (const k of Object.keys(prev)) {
      if (!(k in next)) {
        removedKeys.push(k);
      }
    }
    if (removedKeys.length) {
      const current = getAskAIExtraPageContext();
      for (const k of removedKeys) {
        // only remove if nobody overwrote it since we set it
        if (current[k] === prev[k]) {
          removeAskAIExtraPageContextKey(k);
        }
      }
    }

    lastAppliedRef.current = next;

    return () => {
      // Unmount/cleanup: remove the last applied keys (safely).
      const applied = lastAppliedRef.current;
      const current = getAskAIExtraPageContext();
      for (const [k, v] of Object.entries(applied)) {
        if (current[k] === v) {
          removeAskAIExtraPageContextKey(k);
        }
      }
    };
  }, [extra]);
}
